import React from 'react';
import { Workflow, Plug, Cpu, GitBranch, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { howWeDoItContent } from '../data/mock';

const iconMap = {
  1: Workflow,
  2: Plug,
  3: Cpu,
  4: GitBranch
};

const HowWeDoItSection = () => {
  const navigate = useNavigate();

  return (
    <section id="how-it-works" className="relative bg-black py-32 overflow-hidden">
      {/* Top border accent */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      
      {/* Floating accent */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[300px] h-[600px] bg-[#00FFD1]/3 blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-[#00FFD1] text-lg font-medium tracking-wide">Technology</span>
          <h2 className="text-4xl md:text-5xl font-semibold text-white leading-tight mt-4">
            {howWeDoItContent.title}
          </h2>
          <p className="text-2xl text-white/60 mt-4">
            {howWeDoItContent.subtitle}
          </p>
          <p className="text-lg text-white/70 leading-relaxed mt-6">
            {howWeDoItContent.description}
          </p>
        </div>

        {/* Pipeline Visualization */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent" />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howWeDoItContent.capabilities.map((capability, index) => {
              const IconComponent = iconMap[capability.id];
              return (
                <div key={capability.id} className="relative">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-2 text-[80px] font-bold text-white/[0.03] leading-none select-none">
                    0{index + 1}
                  </div>
                  
                  <div className="relative bg-[#121212] border border-white/10 p-8 h-full group hover:border-[#00FFD1]/30 transition-all duration-400">
                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#00FFD1]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                    
                    <div className="relative z-10">
                      {/* Icon with connector */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-14 h-14 bg-[#00FFD1]/10 flex items-center justify-center">
                          <IconComponent className="w-7 h-7 text-[#00FFD1]" />
                        </div>
                        {index < 3 && (
                          <div className="hidden lg:flex items-center gap-2 text-white/20">
                            <div className="w-8 h-px bg-white/20" />
                            <CheckCircle className="w-4 h-4" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-semibold text-white mb-3">
                        {capability.title}
                      </h3>
                      <p className="text-white/50 text-base leading-relaxed">
                        {capability.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-white/50 mb-6">Ready to transform your content pipeline?</p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-[#00FFD1] text-black px-8 py-4 text-lg font-medium hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] transition-all duration-400"
          >
            Request Demo
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowWeDoItSection;
